import { workoutService } from "../services/workout";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { Exercise } from "../schemas/workout";
import { formatTime } from "../utils/formatTime";
import { useWorkout } from "@/contexts/WorkoutContext";

export function useWorkoutOperations() {
  const { workout, dispatch } = useWorkout();
  const navigate = useNavigate();

  // Format exercise details for display
  const formatExerciseDetails = (exercise: Exercise) => {
    const structureType = exercise.structure.type;
    if (structureType === "sets") {
      return `${exercise.structure.sets} ${
        exercise.structure.sets === 1 ? "set" : "sets"
      } × ${exercise.structure.reps} reps`;
    }

    if (structureType === "timed") {
      return `${exercise.structure.sets} ${
        exercise.structure.sets === 1 ? "set" : "sets"
      } × ${formatTime(exercise.structure.duration)}`;
    }

    if (structureType === "interval") {
      return `${exercise.structure.rounds} ${
        exercise.structure.rounds === 1 ? "round" : "rounds"
      } (${formatTime(exercise.structure.work)}/${formatTime(
        exercise.structure.rest
      )})`;
    }

    if (structureType === "cadence") {
      return `${exercise.structure.sets} ${
        exercise.structure.sets === 1 ? "set" : "sets"
      } × ${exercise.structure.reps} reps (${
        exercise.structure.tempo || "3-1-3"
      })`;
    }

    return "Unknown type";
  };

  // Get exercise sets value
  const getExerciseSets = (exercise: Exercise) => {
    const structureType = exercise.structure.type;
    if (
      structureType === "sets" ||
      structureType === "timed" ||
      structureType === "cadence"
    ) {
      return exercise.structure.sets.toString();
    }
    if (structureType === "interval") {
      return exercise.structure.rounds.toString();
    }
    return "1";
  };

  // Get exercise reps value
  const getExerciseReps = (exercise: Exercise) => {
    const structureType = exercise.structure.type;
    if (structureType === "sets" || structureType === "cadence") {
      return exercise.structure.reps?.toString() || "0";
    }
    return "0";
  };

  // Get exercise duration value
  const getExerciseDuration = (exercise: Exercise) => {
    const structureType = exercise.structure.type;
    if (structureType === "timed") {
      return exercise.structure.duration.toString();
    }
    if (structureType === "interval") {
      return exercise.structure.work.toString();
    }
    return "0";
  };

  // Load workout data
  const loadWorkout = async (id: string) => {
    try {
      dispatch({ type: "SET_LOADING", payload: true });
      const data = await workoutService.getWorkout(id);

      if (data) {
        dispatch({ type: "SET_WORKOUT", payload: data });

        // For new workouts, set isDirty to false since we just created it
        if (
          data.name === "New Workout" &&
          !data.description &&
          data.exercises.length === 0
        ) {
          dispatch({ type: "SET_DIRTY", payload: false });
        }
      }
    } catch (err) {
      console.error("Failed to load workout:", err);
      dispatch({
        type: "SET_ERROR",
        payload: err instanceof Error ? err.message : "Failed to load workout",
      });
    }
  };

  // Save workout
  const saveWorkout = async () => {
    if (!workout) return;

    try {
      dispatch({ type: "SET_SAVING", payload: true });

      // Validate workout before saving
      if (!workout.name.trim()) {
        toast.error("Please enter a workout name");
        return;
      }

      // Validate exercises
      if (workout.exercises.length === 0) {
        toast.error("Please add at least one exercise");
        return;
      }

      // Ensure all exercises have names
      const hasUnnamedExercises = workout.exercises.some(
        (ex) => !ex.name.trim()
      );
      if (hasUnnamedExercises) {
        toast.error("All exercises must have names");
        return;
      }

      await workoutService.updateWorkout(workout.id, workout);
      toast.success("Workout saved successfully");
      dispatch({ type: "SET_DIRTY", payload: false });
    } catch (err) {
      console.error("Failed to save workout:", err);
      toast.error("Failed to save workout");
    } finally {
      dispatch({ type: "SET_SAVING", payload: false });
    }
  };

  // Delete workout
  const deleteWorkout = async () => {
    if (!workout) {
      throw new Error("No workout to delete");
    }

    try {
      await workoutService.deleteWorkout(workout.id);
      toast.success("Workout deleted successfully");
      navigate("/");
      return true;
    } catch (err) {
      console.error("Failed to delete workout:", err);
      toast.error("Failed to delete workout");
      return false;
    }
  };

  return {
    formatExerciseDetails,
    getExerciseSets,
    getExerciseReps,
    getExerciseDuration,
    loadWorkout,
    saveWorkout,
    deleteWorkout,
  };
}
